import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date string or Date object to UK format (DD/MM/YYYY)
 * @param date - Date string, Date object, or null/undefined
 * @param options - Additional formatting options
 * @returns Formatted date string in UK format or fallback text
 */
export function formatDateUK(
  date: string | Date | null | undefined,
  options: {
    fallback?: string;
    includeTime?: boolean;
  } = {}
): string {
  const { fallback = "Unknown", includeTime = false } = options;

  if (!date) return fallback;

  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;

    if (Number.isNaN(dateObj.getTime())) return fallback;

    const formatOptions: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };

    if (includeTime) {
      formatOptions.hour = "2-digit";
      formatOptions.minute = "2-digit";
      formatOptions.hour12 = false; // Use 24-hour format
    }

    return dateObj.toLocaleDateString("en-GB", formatOptions);
  } catch {
    return fallback;
  }
}

/**
 * Formats a numeric amount into a UK currency string (e.g., £89.99).
 * @param amount The amount to format, can be a string or a number.
 * @param currencyCode The ISO currency code (defaults to 'GBP').
 * @param options Optional configuration for a fallback value.
 * @returns A formatted currency string or a fallback string on error.
 */
export function formatCurrencyUK(
  amount: string | number,
  currencyCode = "GBP",
  options: { fallback?: string } = {}
): string {
  const { fallback = "£0.00" } = options;

  try {
    const numericAmount =
      typeof amount === "string" ? Number.parseFloat(amount) : amount;

    if (Number.isNaN(numericAmount)) {
      return fallback;
    }

    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currencyCode,
    }).format(numericAmount);
  } catch {
    return fallback;
  }
}

/**
 * Encodes a Shopify GID for use in URLs by using base64url encoding.
 * @param gid - The Shopify GID (e.g., "gid://shopify/Order/123?key=abc")
 * @returns URL-safe encoded string
 */
export function encodeShopifyGid(gid: string): string {
  return btoa(gid).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/**
 * Decodes a URL-safe encoded Shopify GID back to its original form.
 * @param encoded - The encoded GID string
 * @returns The original Shopify GID
 */
export function decodeShopifyGid(encoded: string): string {
  const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  return atob(base64 + padding);
}
